/**
 * The WhatsApp templates HRMS sends, defined ONCE here so a company never has to configure them in
 * HRMS. A company creates (and gets approved) templates with exactly these codes and variable names in
 * its own Mehery account; the settings screen shows this list with ready-to-paste text.
 *
 * `variables[].source` is the notification payload key, `variables[].name` is the template variable as
 * written in Mehery: `{{data.<name>}}`. Only non-sensitive reminders belong here — never salary,
 * passwords or OTPs (see `isWhatsappEligibleEvent` in `@hrms/notify`).
 *
 * Adding an entry is safe (an unapproved template just fails with a clear reason); renaming a code or
 * variable breaks every company that already created it, so treat both as a public contract.
 */

export interface WhatsappTemplateVariable {
  /** Payload key on the notification, e.g. `employeeName`. */
  readonly source: string;
  /** Variable name inside the Mehery template, `{{data.<name>}}`, e.g. `employee_name`. */
  readonly name: string;
  /** Plain-language meaning, shown next to the template. */
  readonly description: string;
}

export interface WhatsappTemplateDefinition {
  readonly eventKey: string;
  /**
   * Who receives it. `employee` (the default) goes to people in the directory under the company's
   * consent rules. `candidate` goes to an applicant, and only when THEY ticked "send me updates on
   * WhatsApp" and gave that number (recorded on the application); it is never sent without that.
   */
  readonly audience?: 'employee' | 'candidate';
  readonly label: string;
  /** The template name the company must create in Mehery. */
  readonly code: string;
  readonly variables: readonly WhatsappTemplateVariable[];
  /** Suggested message body to paste into Mehery. Companies may reword it; codes/variables must match. */
  readonly sampleText: string;
}

const CORE_TEMPLATES: readonly WhatsappTemplateDefinition[] = [
  {
    eventKey: 'attendance.shift_reminder',
    label: 'Shift starting soon',
    code: 'hrms_attendance_shift_reminder',
    variables: [
      { source: 'employeeName', name: 'employee_name', description: "Employee's name" },
      { source: 'shiftName', name: 'shift_name', description: 'Shift name' },
      { source: 'shiftStartTime', name: 'shift_start_time', description: 'Shift start time' },
      { source: 'date', name: 'date', description: 'Date' },
    ],
    sampleText:
      'Hi {{data.employee_name}}, your {{data.shift_name}} shift starts at {{data.shift_start_time}} on {{data.date}}. Please remember to check in.',
  },
  {
    eventKey: 'attendance.clock_out_reminder',
    label: 'Shift ending soon — clock out reminder',
    code: 'hrms_attendance_clock_out_reminder',
    variables: [
      { source: 'employeeName', name: 'employee_name', description: "Employee's name" },
      { source: 'shiftName', name: 'shift_name', description: 'Shift name' },
      { source: 'shiftEndTime', name: 'shift_end_time', description: 'Shift end time' },
    ],
    sampleText:
      'Hi {{data.employee_name}}, your {{data.shift_name}} shift ends at {{data.shift_end_time}}. Please remember to clock out.',
  },
  {
    eventKey: 'attendance.late_arrival_self',
    label: 'Your late check-in recorded',
    code: 'hrms_attendance_late_checkin',
    variables: [
      { source: 'employeeName', name: 'employee_name', description: "Employee's name" },
      { source: 'date', name: 'date', description: 'Date' },
      {
        source: 'scheduledStartTime',
        name: 'scheduled_start_time',
        description: 'Shift start time',
      },
      {
        source: 'actualClockInTime',
        name: 'actual_clock_in_time',
        description: 'Actual check-in time',
      },
      { source: 'lateMinutes', name: 'late_minutes', description: 'Minutes late' },
    ],
    sampleText:
      'Hi {{data.employee_name}}, you checked in at {{data.actual_clock_in_time}} on {{data.date}}, {{data.late_minutes}} minutes after your {{data.scheduled_start_time}} shift start.',
  },
  // Travel Desk (ADR 0030 D23). Itinerary facts only: no fares, invoice amounts or passport / visa data.
  {
    eventKey: 'travel.booking.confirmed',
    label: 'Travel booking confirmed',
    code: 'hrms_travel_booking_confirmed',
    variables: [
      {
        source: 'reference',
        name: 'trip_reference',
        description: 'Trip reference, e.g. TRV-2026-00012',
      },
      { source: 'modeLabel', name: 'mode', description: 'Kind of booking, e.g. Flight or Hotel' },
      {
        source: 'bookingReference',
        name: 'booking_reference',
        description: 'Booking reference (PNR)',
      },
      { source: 'destination', name: 'destination', description: 'Destination' },
      { source: 'startDate', name: 'start_date', description: 'Trip start date' },
    ],
    sampleText:
      'Your {{data.mode}} booking for trip {{data.trip_reference}} to {{data.destination}} on {{data.start_date}} is confirmed (booking reference {{data.booking_reference}}). The ticket is attached to the trip.',
  },
  {
    eventKey: 'travel.trip.upcoming',
    label: 'Trip tomorrow',
    code: 'hrms_travel_trip_upcoming',
    variables: [
      {
        source: 'reference',
        name: 'trip_reference',
        description: 'Trip reference, e.g. TRV-2026-00012',
      },
      { source: 'destination', name: 'destination', description: 'Destination' },
      { source: 'startDate', name: 'start_date', description: 'Trip start date' },
    ],
    sampleText:
      'Reminder: your trip {{data.trip_reference}} to {{data.destination}} starts on {{data.start_date}}. Open the trip for your itinerary.',
  },
  {
    eventKey: 'travel.trip.closing_soon',
    label: 'Trip closing soon',
    code: 'hrms_travel_trip_closing_soon',
    variables: [
      {
        source: 'reference',
        name: 'trip_reference',
        description: 'Trip reference, e.g. TRV-2026-00012',
      },
      { source: 'closesOn', name: 'closes_on', description: 'Last date to add expenses' },
    ],
    sampleText:
      'Trip {{data.trip_reference}} closes on {{data.closes_on}}. Add any remaining expenses before then.',
  },
];

/** Recruitment (recruitment R7). Time-sensitive and free of pay, so they are safe on WhatsApp. */
const RECRUITMENT_TEMPLATES: readonly WhatsappTemplateDefinition[] = [
  {
    eventKey: 'candidate.interview_scheduled',
    audience: 'candidate',
    label: 'Interview scheduled (candidate)',
    code: 'hrms_candidate_interview_scheduled',
    variables: [
      { source: 'candidateFirstName', name: 'candidate_name', description: "Candidate's first name" },
      { source: 'postingTitle', name: 'role', description: 'Job title' },
      { source: 'roundName', name: 'round', description: 'Interview round' },
      { source: 'scheduledAt', name: 'when', description: 'Date and time' },
      { source: 'meetingUrl', name: 'join_link', description: 'Join link' },
      { source: 'orgName', name: 'company', description: 'Company name' },
    ],
    sampleText:
      'Hi {{data.candidate_name}}, your {{data.round}} interview for {{data.role}} at {{data.company}} is on {{data.when}}. Join link: {{data.join_link}}',
  },
  {
    eventKey: 'candidate.interview_cancelled',
    audience: 'candidate',
    label: 'Interview cancelled (candidate)',
    code: 'hrms_candidate_interview_cancelled',
    variables: [
      { source: 'candidateFirstName', name: 'candidate_name', description: "Candidate's first name" },
      { source: 'postingTitle', name: 'role', description: 'Job title' },
      { source: 'roundName', name: 'round', description: 'Interview round' },
      { source: 'scheduledAt', name: 'when', description: 'The date and time that was booked' },
      { source: 'orgName', name: 'company', description: 'Company name' },
    ],
    sampleText:
      'Hi {{data.candidate_name}}, your {{data.round}} interview for {{data.role}} at {{data.company}} on {{data.when}} has been cancelled. We will be in touch if it is rescheduled.',
  },
  {
    eventKey: 'interview.reminder_candidate',
    audience: 'candidate',
    label: 'Interview today (candidate)',
    code: 'hrms_candidate_interview_reminder',
    variables: [
      { source: 'candidateFirstName', name: 'candidate_name', description: "Candidate's first name" },
      { source: 'postingTitle', name: 'role', description: 'Job title' },
      { source: 'roundName', name: 'round', description: 'Interview round' },
      { source: 'scheduledAt', name: 'when', description: 'Time of the interview' },
      { source: 'meetingUrl', name: 'join_link', description: 'Join link' },
      { source: 'orgName', name: 'company', description: 'Company name' },
    ],
    sampleText:
      'Reminder: {{data.candidate_name}}, your {{data.round}} interview for {{data.role}} at {{data.company}} is today at {{data.when}}. Join link: {{data.join_link}}',
  },
  {
    eventKey: 'interview.scheduled',
    label: 'Interview scheduled (interviewer)',
    code: 'hrms_interview_scheduled',
    variables: [
      { source: 'interviewerName', name: 'interviewer_name', description: "Interviewer's name" },
      { source: 'candidateName', name: 'candidate_name', description: "Candidate's name" },
      { source: 'roundName', name: 'round', description: 'Interview round' },
      { source: 'scheduledAt', name: 'when', description: 'Date and time' },
      { source: 'meetingUrl', name: 'join_link', description: 'Join link' },
    ],
    sampleText:
      'Hi {{data.interviewer_name}}, you are interviewing {{data.candidate_name}} for {{data.round}} on {{data.when}}. Join link: {{data.join_link}}',
  },
  {
    eventKey: 'interview.reminder_panel',
    label: 'Interview today (interviewer)',
    code: 'hrms_interview_reminder_panel',
    variables: [
      { source: 'interviewerName', name: 'interviewer_name', description: "Interviewer's name" },
      { source: 'candidateName', name: 'candidate_name', description: "Candidate's name" },
      { source: 'roundName', name: 'round', description: 'Interview round' },
      { source: 'scheduledAt', name: 'when', description: 'Time of the interview' },
      { source: 'meetingUrl', name: 'join_link', description: 'Join link' },
    ],
    sampleText:
      'Hi {{data.interviewer_name}}, you interview {{data.candidate_name}} ({{data.round}}) today at {{data.when}}. Join link: {{data.join_link}}',
  },
  {
    eventKey: 'interview.feedback_due',
    label: 'Interview feedback due',
    code: 'hrms_interview_feedback_due',
    variables: [
      { source: 'interviewerName', name: 'interviewer_name', description: "Interviewer's name" },
      { source: 'candidateName', name: 'candidate_name', description: "Candidate's name" },
      { source: 'roundName', name: 'round', description: 'Interview round' },
    ],
    sampleText:
      'Hi {{data.interviewer_name}}, please file your scorecard for {{data.candidate_name}} ({{data.round}}) so the team can decide next steps.',
  },
];

/**
 * Live training sessions (training-live.contract.ts). Each invitee's `join_link` is their OWN
 * personal People Orbit link (`/j/:token`), not the meeting provider's link. Meta allows a URL as a
 * body variable; a company that prefers a URL button can create the template that way as long as
 * the variable names match. The absentee alert is deliberately not offered on WhatsApp: it is a
 * remark about a colleague's attendance record, which belongs in the app and email, not a chat app.
 */
const TRAINING_LIVE_TEMPLATES: readonly WhatsappTemplateDefinition[] = [
  {
    eventKey: 'training.live_session.invited',
    label: 'Live training invitation',
    code: 'hrms_training_live_invited',
    variables: [
      { source: 'inviteeName', name: 'employee_name', description: "Employee's first name" },
      { source: 'sessionTitle', name: 'session_title', description: 'Session title' },
      {
        source: 'startsAtLocal',
        name: 'when',
        description: 'Date and time in the session time zone, e.g. Monday, 12 October 2026 at 3:00 pm IST',
      },
      { source: 'hostName', name: 'host_name', description: "Host's name" },
      { source: 'durationLabel', name: 'duration', description: 'Duration, e.g. 1 hour 30 minutes' },
      { source: 'joinUrl', name: 'join_link', description: 'Personal join link' },
    ],
    sampleText:
      'Hi {{data.employee_name}}, you are invited to the live training "{{data.session_title}}" with {{data.host_name}} on {{data.when}} ({{data.duration}}). Join with your personal link: {{data.join_link}}',
  },
  {
    eventKey: 'training.live_session.updated',
    label: 'Live training changed',
    code: 'hrms_training_live_updated',
    variables: [
      { source: 'inviteeName', name: 'employee_name', description: "Employee's first name" },
      { source: 'sessionTitle', name: 'session_title', description: 'Session title' },
      { source: 'startsAtLocal', name: 'when', description: 'New date and time, with time zone' },
      { source: 'durationLabel', name: 'duration', description: 'Duration' },
      { source: 'joinUrl', name: 'join_link', description: 'Personal join link' },
    ],
    sampleText:
      'Hi {{data.employee_name}}, the live training "{{data.session_title}}" has changed. It is now on {{data.when}} ({{data.duration}}). Your join link: {{data.join_link}}',
  },
  {
    eventKey: 'training.live_session.reminder',
    label: 'Live training starting soon',
    code: 'hrms_training_live_reminder',
    variables: [
      { source: 'inviteeName', name: 'employee_name', description: "Employee's first name" },
      { source: 'sessionTitle', name: 'session_title', description: 'Session title' },
      { source: 'startsInLabel', name: 'starts_in', description: 'How soon, e.g. in 15 minutes' },
      { source: 'startsAtLocal', name: 'when', description: 'Date and time, with time zone' },
      { source: 'joinUrl', name: 'join_link', description: 'Personal join link' },
    ],
    sampleText:
      'Reminder: {{data.employee_name}}, the live training "{{data.session_title}}" starts {{data.starts_in}} ({{data.when}}). Join here: {{data.join_link}}',
  },
  {
    eventKey: 'training.live_session.cancelled',
    label: 'Live training cancelled',
    code: 'hrms_training_live_cancelled',
    variables: [
      { source: 'inviteeName', name: 'employee_name', description: "Employee's first name" },
      { source: 'sessionTitle', name: 'session_title', description: 'Session title' },
      { source: 'startsAtLocal', name: 'when', description: 'The date and time that was planned' },
      { source: 'cancelReason', name: 'reason', description: 'Reason for cancelling' },
    ],
    sampleText:
      'Hi {{data.employee_name}}, the live training "{{data.session_title}}" planned for {{data.when}} has been cancelled. Reason: {{data.reason}}',
  },
];

export const WHATSAPP_TEMPLATES: readonly WhatsappTemplateDefinition[] = [
  ...CORE_TEMPLATES,
  ...RECRUITMENT_TEMPLATES,
  ...TRAINING_LIVE_TEMPLATES,
];

export function whatsappTemplateFor(eventKey: string): WhatsappTemplateDefinition | undefined {
  return WHATSAPP_TEMPLATES.find((t) => t.eventKey === eventKey);
}

